import { cloneElement, forwardRef, isValidElement, useId } from 'react'

/**
 * Form-kit (R11 design system) — dekt de formulier-pagina's (A0: 107).
 * Wrapt de echte index.css-classes r11-field / r11-label / r11-input /
 * r11-select / r11-textarea (B0).
 *
 * Exports: FormField, Input, Select, Textarea, FormActions.
 */

/**
 * Input — tekstinput met r11-input. Forwardt ref + alle native props.
 */
export const Input = forwardRef(function Input({ className = '', ...rest }, ref) {
  return <input ref={ref} className={`r11-input ${className}`} {...rest} />
})

/**
 * Select — selectie met r11-select. Geef <option>'s als children.
 */
export const Select = forwardRef(function Select({ className = '', children, ...rest }, ref) {
  return (
    <select ref={ref} className={`r11-select ${className}`} {...rest}>
      {children}
    </select>
  )
})

/**
 * Textarea — meerregelig veld met r11-textarea.
 */
export const Textarea = forwardRef(function Textarea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`r11-textarea ${className}`} {...rest} />
})

/**
 * FormField — label + control + hint/error, met a11y-koppeling.
 *
 * Props:
 *   label     — labeltekst (optioneel)
 *   required  — toont een * (optioneel)
 *   hint      — subtiele hulptekst onder het veld (optioneel)
 *   error     — foutmelding (rood); vervangt de hint en zet aria-invalid
 *   htmlFor   — expliciete id (anders auto via useId)
 *   children  — het control (Input/Select/Textarea of een eigen element)
 *   className — extra classes op de r11-field-wrapper
 *
 * Koppelt label <-> control automatisch (id + aria-describedby + aria-invalid)
 * als children één geldig element is.
 */
export function FormField({
  label,
  required = false,
  hint,
  error,
  htmlFor,
  children,
  className = '',
}) {
  const autoId = useId()
  const controlId = (isValidElement(children) && children.props.id) || htmlFor || autoId
  const hintId = hint && !error ? `${controlId}-hint` : undefined
  const errId = error ? `${controlId}-err` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined

  let control = children
  if (isValidElement(children)) {
    control = cloneElement(children, {
      id: controlId,
      'aria-invalid': error ? true : children.props['aria-invalid'],
      'aria-describedby': describedBy ?? children.props['aria-describedby'],
    })
  }

  return (
    <div className={`r11-field ${className}`}>
      {label && (
        <label htmlFor={controlId} className="r11-label">
          {label}
          {required && <span className="text-red-600" aria-hidden="true"> *</span>}
        </label>
      )}
      {control}
      {hint && !error && <p id={hintId} className="text-xs text-muted">{hint}</p>}
      {error && <p id={errId} className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

/**
 * FormActions — knoppenrij onder een formulier.
 *
 * Props:
 *   align     — 'right' (default) | 'left' | 'between'
 *   children  — meestal <Button>'s
 *   className — extra classes
 */
export function FormActions({ children, align = 'right', className = '' }) {
  const justify =
    align === 'left' ? 'justify-start' : align === 'between' ? 'justify-between' : 'justify-end'
  return <div className={`flex items-center gap-3 ${justify} ${className}`}>{children}</div>
}
